import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { buildFeatureCounts } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { useDashboardDateRange, type DashboardRange } from '@/features/dashboard/hooks';
import { queryFeatureSummary } from '@/features/features/data';
import {
  buildStatusMetricCards,
  buildStatusDeltaMap,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import {
  queryKeys,
  useAccessToken,
  useOrganisationEntitlements,
  usePersistedStringArray,
  useResolvedFeatureFlags,
  useTagOptions,
} from '@/shared/hooks';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { buildGridQueryInput, type GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureStatus } from '@flowqa/shared';

type UseFeaturesPageViewModelResult = {
  range: DashboardRange;
  setRange: (value: DashboardRange) => void;
  selectedStatuses: FeatureStatus[];
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  onStatusToggle: (status: FeatureStatus) => void;
  onTagsChange: (nextTags: string[]) => void;
  tableFilters: GridQueryInput['filters'];
  featureMetricCards: ReturnType<typeof buildStatusMetricCards>;
  dateFiltersEnabled: boolean;
  showPageSkeleton: boolean;
};

export const useFeaturesPageViewModel = (): UseFeaturesPageViewModelResult => {
  const accessToken = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [range, setRangeState] = useState<DashboardRange>('latest');
  const [selectedStatuses, setSelectedStatuses] = useState<FeatureStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:features');
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: selectedProjectId,
    enabled: Boolean(accessToken),
  });
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useDashboardDateRange(range);
  const isAllMode = range === 'all';
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const deltasEnabled = featureFlags?.deltas ?? true;
  const dateFiltersEnabled = featureFlags?.dateFilters ?? true;
  const showDelta = deltasEnabled && !isAllMode && previousRangeStartIso !== null;

  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const summaryFilters = useMemo(
    () => mergeFilters(buildDateRangeFilters('createdAt', rangeStartIso, rangeEndIso), tagFilters),
    [rangeEndIso, rangeStartIso, tagFilters],
  );
  const previousSummaryFilters = useMemo(
    () => buildDateRangeFilters('createdAt', previousRangeStartIso, previousRangeEndIso),
    [previousRangeEndIso, previousRangeStartIso],
  );
  const featureSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: summaryFilters,
      }),
    [summaryFilters],
  );
  const previousFeatureSummaryInput = useMemo(
    () =>
      buildGridQueryInput({
        page: 1,
        pageSize: 1,
        sorts: [],
        filters: previousSummaryFilters,
      }),
    [previousSummaryFilters],
  );
  const featureSummaryFiltersKey = useMemo(
    () => JSON.stringify(featureSummaryInput.filters),
    [featureSummaryInput.filters],
  );
  const previousFeatureSummaryFiltersKey = useMemo(
    () => JSON.stringify(previousFeatureSummaryInput.filters),
    [previousFeatureSummaryInput.filters],
  );
  const featureSummaryQuery = useQuery({
    queryKey: queryKeys.featureSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      featureSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryFeatureSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        featureSummaryInput,
        accessToken,
      ),
  });
  const previousFeatureSummaryQuery = useQuery({
    queryKey: queryKeys.featureSummary(
      selectedProjectId ?? null,
      selectedOrganisationId ?? null,
      previousFeatureSummaryFiltersKey,
    ),
    enabled: Boolean(accessToken) && deliveryReady && deliveryEnabled,
    queryFn: async () =>
      queryFeatureSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousFeatureSummaryInput,
        accessToken,
      ),
  });
  const featureSummary = featureSummaryQuery.data ?? null;
  const previousFeatureSummary = previousFeatureSummaryQuery.data ?? null;
  const featureCounts = useMemo(() => buildFeatureCounts(featureSummary), [featureSummary]);
  const previousFeatureCounts = useMemo(
    () => buildFeatureCounts(previousFeatureSummary),
    [previousFeatureSummary],
  );
  const featureDeltas = useMemo(
    () =>
      showDelta && previousFeatureSummary
        ? buildStatusDeltaMap(featureCounts, previousFeatureCounts)
        : undefined,
    [featureCounts, previousFeatureCounts, previousFeatureSummary, showDelta],
  );
  const featureMetricOptions = useMemo(
    () => mapStatusOptionsToMetricOptions(featureStatusOptions, resolveMetricTone),
    [],
  );
  const featureMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: featureMetricOptions,
        counts: featureCounts,
        deltas: featureDeltas,
        total: featureSummary?.total,
        loading: featureSummaryQuery.isLoading,
        showDelta,
      }),
    [
      featureCounts,
      featureDeltas,
      featureMetricOptions,
      featureSummary?.total,
      featureSummaryQuery.isLoading,
      showDelta,
    ],
  );
  const statusFilters = useMemo(
    () => buildInFilter('status', selectedStatuses),
    [selectedStatuses],
  );
  const tableFilters = useMemo(
    () => mergeFilters(summaryFilters, statusFilters, tagFilters),
    [statusFilters, summaryFilters, tagFilters],
  );
  const onStatusToggle = useCallback((status: FeatureStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);

  const showPageSkeleton = Boolean(accessToken) && featureSummaryQuery.isLoading && !featureSummary;

  const setRange = useCallback((value: DashboardRange) => {
    setRangeState(value);
  }, []);

  return {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    tagOptions,
    tagOptionsLoading,
    onStatusToggle,
    onTagsChange: setSelectedTags,
    tableFilters,
    featureMetricCards,
    dateFiltersEnabled,
    showPageSkeleton,
  };
};
